import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Users, Lock, LogIn, Mail } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function StaffLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please enter your email and password');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/auth/login`, { username: email, password });
      if (res.data.success) {
        if (res.data.role !== 'staff') {
          toast.error('This page is for staff only — please use the Admin login page');
          return;
        }
        localStorage.setItem('njk_admin_token', res.data.token);
        localStorage.setItem('njk_admin_user', res.data.username);
        localStorage.setItem('njk_admin_role', 'staff');
        localStorage.setItem('njk_full_billing', res.data.full_billing === false ? 'false' : 'true');
        toast.success(`Welcome, ${res.data.username}`);
        navigate('/admin/billing');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4" data-testid="staff-login-page">
      <Card className="w-full max-w-md shadow-xl border-0">
        <CardHeader className="text-center bg-[#5c1a1a] text-white rounded-t-lg py-8">
          <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Users className="text-white" size={28} />
          </div>
          <CardTitle className="text-2xl">Staff Login</CardTitle>
          <p className="text-white/70 text-sm mt-1">NJK Logistics Management System</p>
        </CardHeader>
        <CardContent className="pt-8 pb-8 px-8">
          <form onSubmit={handleLogin} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="staff-email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 text-gray-400" size={16} />
                <Input
                  id="staff-email"
                  data-testid="staff-email-login-input"
                  type="email"
                  className="pl-10"
                  placeholder="Enter your staff email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="username"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="staff-password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 text-gray-400" size={16} />
                <Input
                  id="staff-password"
                  data-testid="staff-password-login-input"
                  type="password"
                  className="pl-10"
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                />
              </div>
            </div>
            <Button
              type="submit"
              className="w-full bg-[#5c1a1a] hover:bg-[#7a2525] text-white h-11"
              disabled={loading}
              data-testid="staff-login-btn"
            >
              {loading ? 'Logging in...' : <><LogIn size={18} className="mr-2" /> Sign In</>}
            </Button>
          </form>
          <p className="text-center text-xs text-gray-400 mt-6">
            Forgot your password? Ask your manager to reset it.
          </p>
          <p className="text-center text-xs text-gray-400 mt-1">
            Administrator? <Link to="/admin/login" className="underline text-[#5c1a1a]">Admin login</Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
